import { Box } from "@mui/material";
import CarouselCard from "Components/UI/CarouselCard";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import { StandardAnalyticsChartStatCard } from "features/analytics";
import {
  CHART_KEY_DASHBOARD_STORE_OVERVIEW_AOV,
  CHART_KEY_DASHBOARD_STORE_OVERVIEW_CUSTOMERS,
  CHART_KEY_DASHBOARD_STORE_OVERVIEW_ITEMS_SOLD,
  CHART_KEY_DASHBOARD_STORE_OVERVIEW_ORDERS,
  CHART_KEY_DASHBOARD_STORE_OVERVIEW_REVENUE_TREND,
  CHART_KEY_DASHBOARD_STORE_OVERVIEW_TOP_PRODUCTS,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";
import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiOperator,
  ApiYAxisChartType,
} from "@incendium/api";

interface IStoreOverviewTabProps {
  attributes: ApiChartAttribute[];
}

function StoreOverviewTab({ attributes }: IStoreOverviewTabProps) {
  return (
    <>
      <CarouselCard
        title="Key Stats"
        emptyMessage="No key stats available"
        items={[
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_STORE_OVERVIEW_ORDERS}
            staticAttributes={attributes}
            chart={{
              name: "Orders",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SALES_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "Orders",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_STORE_OVERVIEW_ITEMS_SOLD}
            staticAttributes={attributes}
            chart={{
              name: "Items Sold",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SALE_ITEMS_QUANTITY],
                },
              ],
              displayOptions: {
                statTitle: "Items Sold",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_STORE_OVERVIEW_AOV}
            staticAttributes={attributes}
            chart={{
              name: "Average Order Value",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_AVERAGE_SALE_VALUE],
                },
              ],
              displayOptions: {
                statTitle: "Average Order Value",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_STORE_OVERVIEW_CUSTOMERS}
            staticAttributes={attributes}
            chart={{
              name: "Customers",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_CUSTOMERS_COUNT],
                },
              ],
              displayOptions: {
                statTitle: "Customers",
              },
              comparison: true,
            }}
          />,
        ]}
        loading={false}
      />

      <Box sx={{ height: 400, flexShrink: 0 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_STORE_OVERVIEW_REVENUE_TREND}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Revenue Over Time",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_REVENUE],
                chart: {
                  [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.BAR,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
              availableMetrics: [
                ApiMetric.METRIC_REVENUE,
                ApiMetric.METRIC_SALES_COUNT,
                ApiMetric.METRIC_AVERAGE_SALE_VALUE,
              ],
            },
          }}
        />
      </Box>

      <Box sx={{ height: table5Rows, flexShrink: 0 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_STORE_OVERVIEW_TOP_PRODUCTS}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Top Products",
            dimension: [ApiDimension.DIMENSION_PRODUCT_NAME],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiMetric.METRIC_PRODUCT_REVENUE} DESC`,
            having: [
              {
                metric: ApiMetric.METRIC_PRODUCT_REVENUE,
                operator: ApiOperator.IS_GREATER_THAN,
                value: 0,
              },
            ],
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SALE_ITEMS_QUANTITY,
                  ApiMetric.METRIC_PRODUCT_REVENUE,
                ],
              },
            ],
            displayOptions: {
              noAttributionToggle: true,
              noFilters: true,
              rowsPerPage: 0,
              showTotals: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default StoreOverviewTab;
